import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  isPeriodicSyncSupported,
  registerPeriodicSync,
  unregisterPeriodicSync,
  isPeriodicSyncRegistered,
} from '@/lib/periodicSync';
import { RefreshCw, Info, Clock } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function PeriodicSyncSettings() {
  const [isSupported, setIsSupported] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [syncInterval, setSyncInterval] = useState('12');

  useEffect(() => {
    const checkSupport = async () => {
      const supported = isPeriodicSyncSupported();
      setIsSupported(supported);

      if (supported) {
        const registered = await isPeriodicSyncRegistered();
        setIsEnabled(registered);
      }
    };

    checkSupport();
  }, []);

  const handleToggle = async (enabled: boolean) => {
    setIsLoading(true);

    try {
      if (enabled) {
        const intervalMs = parseInt(syncInterval) * 60 * 60 * 1000;
        const success = await registerPeriodicSync({ minInterval: intervalMs });

        if (success) {
          setIsEnabled(true);
          toast.success('Background sync enabled', {
            description: `Your data will sync automatically every ${syncInterval} hours`,
          });
        } else {
          toast.error('Failed to enable background sync', {
            description: 'Please check browser permissions',
          });
        }
      } else {
        const success = await unregisterPeriodicSync();

        if (success) {
          setIsEnabled(false);
          toast.success('Background sync disabled');
        } else {
          toast.error('Failed to disable background sync');
        }
      }
    } catch (error) {
      console.error('Error toggling periodic sync:', error);
      toast.error('An error occurred', {
        description: 'Please try again later',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleIntervalChange = async (value: string) => {
    setSyncInterval(value);

    // If already enabled, re-register with new interval
    if (isEnabled) {
      setIsLoading(true);
      try {
        await unregisterPeriodicSync();
        const intervalMs = parseInt(value) * 60 * 60 * 1000;
        await registerPeriodicSync({ minInterval: intervalMs });
        toast.success('Sync interval updated', {
          description: `Background sync will occur every ${value} hours`,
        });
      } catch (error) {
        console.error('Error updating interval:', error);
        toast.error('Failed to update sync interval');
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (!isSupported) {
    return (
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            <h3 className="text-lg font-semibold">Background Data Sync</h3>
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Periodic background sync is not supported in your browser. This
              feature requires Chrome 80+ or Edge 80+.
            </AlertDescription>
          </Alert>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Background Data Sync</h3>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="periodic-sync">Enable Background Sync</Label>
              <p className="text-sm text-muted-foreground">
                Automatically sync your data in the background even when the app
                is closed
              </p>
            </div>
            <Switch
              id="periodic-sync"
              checked={isEnabled}
              onCheckedChange={handleToggle}
              disabled={isLoading}
            />
          </div>

          {isEnabled && (
            <div className="space-y-2">
              <Label htmlFor="sync-interval">Sync Interval</Label>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <Select
                  value={syncInterval}
                  onValueChange={handleIntervalChange}
                  disabled={isLoading}
                >
                  <SelectTrigger id="sync-interval" className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">Every 6 hours</SelectItem>
                    <SelectItem value="12">Every 12 hours</SelectItem>
                    <SelectItem value="24">Every 24 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                Note: Actual sync frequency may vary based on device usage and
                battery level
              </p>
            </div>
          )}

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-xs">
              Background sync will keep your opportunities, emails, and tasks
              up-to-date automatically. The browser controls exact timing based
              on usage patterns and battery level.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    </Card>
  );
}
